import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const NotificationContext = createContext(null);

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshUnread = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      return;
    }
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnreadCount(data.count || 0);
    } catch {
      // logged out mid-poll, or a network hiccup — leave the count as-is
    }
  }, [user]);

  useEffect(() => {
    refreshUnread();
    if (!user) return;
    const t = setInterval(refreshUnread, 45000);
    return () => clearInterval(t);
  }, [user, refreshUnread]);

  // Subscribes this browser to push. Safe to call repeatedly — reuses an
  // existing subscription if there is one. Returns false (without throwing)
  // if the user declines, push isn't supported, or the server hasn't set
  // up VAPID keys yet.
  const subscribeToPush = useCallback(async () => {
    if (!user || !("serviceWorker" in navigator) || !("PushManager" in window)) return false;
    try {
      if (Notification.permission === "default") {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") return false;
      }
      if (Notification.permission !== "granted") return false;

      const { data } = await api.get("/push/vapid-public-key");
      if (!data.key) return false; // push not configured server-side yet

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.key),
        });
      }
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      return true;
    } catch {
      return false;
    }
  }, [user]);

  useEffect(() => {
    // If permission was already granted earlier (e.g. via the install
    // tutorial), silently keep the subscription alive without re-prompting.
    if (user && "Notification" in window && Notification.permission === "granted") {
      subscribeToPush();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <NotificationContext.Provider value={{ unreadCount, refreshUnread, subscribeToPush }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
